import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  LifeBuoy, RefreshCw, Loader2, ArrowRight, ShieldCheck, CheckCircle2,
  AlertTriangle, RotateCcw, Database, Calendar, MessageSquareText,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const h = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function DataRecoveryDashboard() {
  const [ibs, setIbs] = useState([]);
  const [selectedIb, setSelectedIb] = useState('');
  const [fromDate, setFromDate] = useState('2026-01-01');
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [strategy, setStrategy] = useState('merge');
  const [scanResult, setScanResult] = useState(null);
  const [backups, setBackups] = useState([]);
  const [restoring, setRestoring] = useState('');

  // WhatsApp paste parser state
  const [waText, setWaText] = useState('');
  const [waParsed, setWaParsed] = useState(null);
  const [waParsing, setWaParsing] = useState(false);
  const [waApplying, setWaApplying] = useState(false);
  const [waTargetIb, setWaTargetIb] = useState('');
  const [waStrategy, setWaStrategy] = useState('overwrite');

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const [ibsRes, bkRes] = await Promise.all([
          axios.get(`${API}/ib/list`, h()),
          axios.get(`${API}/recovery/backups`, h()).catch(() => ({ data: { backups: [] } })),
        ]);
        if (!mounted) return;
        // /ib/list returns { ibs: [...] } or array directly
        const list = Array.isArray(ibsRes.data) ? ibsRes.data : (ibsRes.data.ibs || []);
        setIbs(list);
        setBackups(bkRes.data.backups || []);
      } catch { /* ignore */ }
    };
    load();
    return () => { mounted = false; };
  }, []);

  const runScan = async () => {
    if (!selectedIb) {
      toast.error('Pilih channel dahulu');
      return;
    }
    setScanning(true);
    setScanResult(null);
    try {
      // Step 1: Submit background job (returns immediately)
      const submitRes = await axios.post(
        `${API}/recovery/telegram-scan?ib_id=${selectedIb}&from=${fromDate}&to=${toDate}&limit_messages=500`,
        {},
        { ...h(), timeout: 30000 }
      );
      const jobId = submitRes.data.job_id;
      if (!jobId) throw new Error('No job_id returned');
      toast.info(`Scan job started… polling progress`);

      // Step 2: Poll status every 3s, max 5 min
      const maxAttempts = 100; // 5 minutes
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise(r => setTimeout(r, 3000));
        const stRes = await axios.get(`${API}/recovery/scan-status/${jobId}`, h());
        const job = stRes.data;
        if (job.status === 'running' && job.progress) {
          // Update toast (or could use a state for progress display)
          if (i % 5 === 0) {
            toast.info(`Scanning… ${job.progress.messages_scanned} msgs, ${job.progress.sales_dates} sales dates found`);
          }
          continue;
        }
        if (job.status === 'done' && job.result) {
          setScanResult(job.result);
          toast.success(
            `Scan complete: ${job.result.sales_dates_recovered} sales + ${job.result.marketing_dates_recovered} marketing dates parsed`
          );
          return;
        }
        if (job.status === 'failed') {
          throw new Error(job.error || 'Scan job failed');
        }
      }
      throw new Error('Scan timed out after 5 minutes');
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(`Scan failed: ${detail}`);
      if (detail && detail.toLowerCase().includes('mtproto')) {
        toast.info('Re-authenticate MTProto first: Admin Panel → TG Reports → MTProto Health → Re-Authenticate');
      }
    } finally {
      setScanning(false);
    }
  };

  const runApply = async () => {
    if (!scanResult) return;
    if (!window.confirm(
      `Apply recovery?\n\n${scanResult.sales_dates_recovered} sales + ${scanResult.marketing_dates_recovered} marketing entries\nStrategy: ${strategy === 'merge' ? 'MERGE (keep existing, fill in missing)' : 'REPLACE (overwrite)'}\n\nA backup will be automatically saved BEFORE applying.`
    )) return;
    setApplying(true);
    try {
      const res = await axios.post(
        `${API}/recovery/telegram-apply`,
        {
          ib_id: scanResult.ib_id,
          sales_entries: scanResult.all_sales_entries,
          marketing_entries: scanResult.all_marketing_entries,
          strategy,
        },
        { ...h(), timeout: 60000 }
      );
      const c = res.data.counts;
      toast.success(
        `Recovery applied! Docs created=${c.docs_created} updated=${c.docs_updated} sales=${c.sales_added} marketing=${c.marketing_added}`
      );
      if (res.data.backup_collection) {
        toast.info(`Backup saved as: ${res.data.backup_collection}`);
      }
      // Reload backups list
      const bkRes = await axios.get(`${API}/recovery/backups`, h()).catch(() => ({ data: { backups: [] } }));
      setBackups(bkRes.data.backups || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  const restoreBackup = async (name) => {
    if (!window.confirm(`Restore from ${name}?\n\nThis will OVERWRITE current daily_data_2026 for the backed-up months. Use only if recovery went wrong.`)) return;
    setRestoring(name);
    try {
      const res = await axios.post(`${API}/recovery/restore`, { backup_collection: name }, h());
      toast.success(`Restored ${res.data.restored_docs} docs from ${name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoring('');
    }
  };

  const parseWA = async () => {
    if (!waText.trim()) {
      toast.error('Paste WhatsApp messages first');
      return;
    }
    setWaParsing(true);
    setWaParsed(null);
    try {
      const res = await axios.post(`${API}/recovery/whatsapp-parse`, { text: waText }, h());
      setWaParsed(res.data);
      toast.success(`Parsed ${res.data.total_parsed} entries from ${res.data.total_blocks} blocks`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Parse failed');
    } finally {
      setWaParsing(false);
    }
  };

  const applyWA = async () => {
    if (!waParsed || !waTargetIb) {
      toast.error('Parse first AND pick target channel');
      return;
    }
    const entries = waParsed.entries || [];
    if (entries.length === 0) {
      toast.error('No entries to apply');
      return;
    }
    if (!window.confirm(`Apply ${entries.length} entries to channel?\n\nStrategy: ${waStrategy.toUpperCase()}\n• MERGE: skip existing dates\n• OVERWRITE: replace ALL existing entries (with backup)\n• FILL_ZEROS: replace only existing entries where all values = 0\n\nBackup will be saved before write.`)) return;
    setWaApplying(true);
    try {
      const res = await axios.post(`${API}/recovery/whatsapp-apply`, { ib_id: waTargetIb, entries, strategy: waStrategy }, h());
      const c = res.data.counts;
      toast.success(`Applied: ${c.sales_added} added, ${c.sales_overwritten || 0} overwritten, ${c.sales_skipped || 0} skipped. Docs: created=${c.docs_created} updated=${c.docs_updated}`);
      if (res.data.backup_collection) toast.info(`Backup: ${res.data.backup_collection}`);
      // Refresh backups
      const bkRes = await axios.get(`${API}/recovery/backups`, h()).catch(() => ({ data: { backups: [] } }));
      setBackups(bkRes.data.backups || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Apply failed');
    } finally {
      setWaApplying(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="data-recovery-dashboard">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-red-700 flex items-center justify-center">
          <LifeBuoy size={18} className="text-white" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-white">Data Recovery — Telegram Scraper</h2>
          <p className="text-xs text-[hsl(var(--text-muted))]">
            Recover wiped daily data by scraping past Telegram report messages. Backup-first, dry-run preview.
          </p>
        </div>
      </div>

      {/* Step indicator */}
      <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-[11px] text-amber-200/80 leading-relaxed">
        <strong className="text-amber-400">⚠️ Safety:</strong> Scan dahulu (dry-run, takkan tulis DB). Review preview, kemudian klik Apply.
        Apply akan auto-backup data sedia ada dulu sebelum tulis baru — anda boleh restore kalau silap.
      </div>

      <Tabs defaultValue="telegram" className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="telegram" data-testid="tab-recovery-telegram">
            <RefreshCw size={12} className="mr-1.5" />Telegram Scraper
          </TabsTrigger>
          <TabsTrigger value="whatsapp" data-testid="tab-recovery-whatsapp">
            <MessageSquareText size={12} className="mr-1.5" />WhatsApp Paste
          </TabsTrigger>
        </TabsList>

        <TabsContent value="telegram" className="space-y-3 mt-3">

      {/* SCAN SECTION */}
      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-4 space-y-3">
        <h3 className="text-sm font-bold text-white flex items-center gap-2">
          <RefreshCw size={14} className="text-blue-400" />
          Step 1: Scan Telegram Channel
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))]">Channel</label>
            <Select value={selectedIb} onValueChange={setSelectedIb}>
              <SelectTrigger className="h-9 mt-1 text-[12px]" data-testid="recovery-channel-select">
                <SelectValue placeholder="-- Pilih channel --" />
              </SelectTrigger>
              <SelectContent>
                {ibs.map(ib => (
                  <SelectItem key={ib.id} value={ib.id}>{ib.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))]">From Date</label>
            <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="h-9 mt-1" data-testid="recovery-from" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))]">To Date</label>
            <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="h-9 mt-1" data-testid="recovery-to" />
          </div>
          <div className="flex items-end">
            <Button onClick={runScan} disabled={scanning || !selectedIb} className="w-full bg-blue-600 hover:bg-blue-700 h-9" data-testid="recovery-scan-btn">
              {scanning ? <Loader2 size={14} className="animate-spin mr-2" /> : <RefreshCw size={14} className="mr-2" />}
              Scan Now
            </Button>
          </div>
        </div>
      </div>

      {/* SCAN RESULT */}
      {scanResult && (
        <div className="rounded-xl border border-blue-500/30 bg-blue-500/5 p-4 space-y-3" data-testid="recovery-result">
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <CheckCircle2 size={14} className="text-blue-400" />
            Scan Result — {scanResult.ib_name}
          </h3>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[12px]">
            <div className="bg-[hsl(var(--card))] rounded-lg p-2.5">
              <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-muted))]">Messages Scanned</div>
              <div className="font-mono text-base text-white font-bold">{scanResult.messages_scanned}</div>
            </div>
            <div className="bg-[hsl(var(--card))] rounded-lg p-2.5">
              <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-muted))]">Sales Dates Recovered</div>
              <div className="font-mono text-base text-emerald-400 font-bold">{scanResult.sales_dates_recovered}</div>
            </div>
            <div className="bg-[hsl(var(--card))] rounded-lg p-2.5">
              <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-muted))]">Marketing Dates Recovered</div>
              <div className="font-mono text-base text-emerald-400 font-bold">{scanResult.marketing_dates_recovered}</div>
            </div>
            <div className="bg-[hsl(var(--card))] rounded-lg p-2.5">
              <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-muted))]">Date Range Found</div>
              <div className="font-mono text-[11px] text-white">
                {scanResult.earliest_date || '—'} <span className="text-[hsl(var(--text-muted))]">→</span> {scanResult.latest_date || '—'}
              </div>
            </div>
          </div>

          {scanResult.preview_by_month && Object.keys(scanResult.preview_by_month).length > 0 && (
            <div>
              <h4 className="text-[11px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))] mb-2">Per-Month Breakdown</h4>
              <div className="overflow-x-auto rounded-md border border-[hsl(var(--border))]">
                <table className="w-full text-[11px]">
                  <thead className="bg-[hsl(var(--card))] text-[10px] uppercase tracking-wider text-[hsl(var(--text-muted))]">
                    <tr>
                      <th className="text-left px-3 py-1.5">Month</th>
                      <th className="text-right px-3 py-1.5">Sales Days</th>
                      <th className="text-right px-3 py-1.5">Marketing Days</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(scanResult.preview_by_month).map(([m, v]) => (
                      <tr key={m} className="border-t border-[hsl(var(--border))]">
                        <td className="px-3 py-1.5 font-mono text-white">{m}</td>
                        <td className="px-3 py-1.5 text-right font-mono text-emerald-400">{v.sales_count}</td>
                        <td className="px-3 py-1.5 text-right font-mono text-blue-400">{v.marketing_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* SALES PREVIEW ROWS — verify per-day commission/leads/brokers BEFORE applying */}
          {scanResult.sales_preview_rows && scanResult.sales_preview_rows.length > 0 && (
            <div>
              <h4 className="text-[11px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))] mb-2">
                Sales Preview ({scanResult.sales_preview_rows.length} days)
              </h4>
              <div className="overflow-x-auto rounded-md border border-[hsl(var(--border))] max-h-80 overflow-y-auto">
                <table className="w-full text-[11px]" data-testid="recovery-sales-preview-table">
                  <thead className="bg-[hsl(var(--card))] text-[10px] uppercase tracking-wider text-[hsl(var(--text-muted))] sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1.5">Date</th>
                      <th className="text-right px-2 py-1.5">Leads</th>
                      <th className="text-right px-2 py-1.5">New Cls</th>
                      <th className="text-right px-2 py-1.5">Ret Cls</th>
                      <th className="text-right px-2 py-1.5">Total Cls</th>
                      <th className="text-right px-2 py-1.5">Greet</th>
                      <th className="text-right px-2 py-1.5">1tick</th>
                      <th className="text-right px-2 py-1.5">Reg</th>
                      <th className="text-right px-2 py-1.5">Comm $</th>
                      <th className="text-right px-2 py-1.5">Depo $</th>
                      <th className="text-left px-2 py-1.5">Brokers</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scanResult.sales_preview_rows.map((r) => (
                      <tr key={r.date} className="border-t border-[hsl(var(--border))]">
                        <td className="px-2 py-1.5 font-mono text-white">{r.date}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-amber-400">{r.totalLead}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-blue-400">{r.newClosed}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-purple-400">{r.vipClosed}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-emerald-400">{r.totalClosed}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-[hsl(var(--text-muted))]">{r.greeting}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-[hsl(var(--text-muted))]">{r.oneTick}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-[hsl(var(--text-muted))]">{r.registration}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-emerald-300">${Number(r.total_commission_usd || 0).toFixed(2)}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-cyan-300">${Number(r.total_deposit_usd || 0).toFixed(2)}</td>
                        <td className="px-2 py-1.5 text-[10px] text-[hsl(var(--text-muted))]">
                          {(r.brokers_list || []).length > 0
                            ? r.brokers_list.map((b) => `${b.name}:$${Number(b.commission_usd || 0).toFixed(0)}`).join(', ')
                            : <span className="text-amber-400">— no brokers parsed</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* MARKETING PREVIEW ROWS */}
          {scanResult.marketing_preview_rows && scanResult.marketing_preview_rows.length > 0 && (
            <div>
              <h4 className="text-[11px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))] mb-2">
                Marketing Preview ({scanResult.marketing_preview_rows.length} days)
              </h4>
              <div className="overflow-x-auto rounded-md border border-[hsl(var(--border))] max-h-80 overflow-y-auto">
                <table className="w-full text-[11px]" data-testid="recovery-marketing-preview-table">
                  <thead className="bg-[hsl(var(--card))] text-[10px] uppercase tracking-wider text-[hsl(var(--text-muted))] sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1.5">Date</th>
                      <th className="text-right px-2 py-1.5">Spend RM</th>
                      <th className="text-right px-2 py-1.5">Imp</th>
                      <th className="text-right px-2 py-1.5">Clicks</th>
                      <th className="text-right px-2 py-1.5">LPV</th>
                      <th className="text-right px-2 py-1.5">CTA</th>
                      <th className="text-right px-2 py-1.5">Subs TG</th>
                      <th className="text-right px-2 py-1.5">Subs Bot</th>
                      <th className="text-right px-2 py-1.5">Leads</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scanResult.marketing_preview_rows.map((r) => (
                      <tr key={r.date} className="border-t border-[hsl(var(--border))]">
                        <td className="px-2 py-1.5 font-mono text-white">{r.date}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-emerald-300">{Number(r.spend || 0).toFixed(2)}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-blue-400">{r.impressions}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-blue-300">{r.clicks}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-[hsl(var(--text-muted))]">{r.lpv}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-[hsl(var(--text-muted))]">{r.cta}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-cyan-300">{r.subsTG}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-cyan-300">{r.subsBot}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-amber-400">{r.leads}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* APPLY SECTION */}
          {(scanResult.sales_dates_recovered > 0 || scanResult.marketing_dates_recovered > 0) && (
            <div className="border-t border-blue-500/20 pt-3">
              <h4 className="text-sm font-bold text-white flex items-center gap-2 mb-2">
                <ArrowRight size={14} className="text-emerald-400" />
                Step 2: Apply to Database
              </h4>
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <label className="text-[11px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))]">Strategy:</label>
                <label className="flex items-center gap-1 text-[12px] cursor-pointer">
                  <input type="radio" name="strategy" value="merge" checked={strategy === 'merge'} onChange={() => setStrategy('merge')} />
                  <span className="text-white"><b>Merge</b> (safe — only fill in dates that don&apos;t exist)</span>
                </label>
                <label className="flex items-center gap-1 text-[12px] cursor-pointer">
                  <input type="radio" name="strategy" value="replace" checked={strategy === 'replace'} onChange={() => setStrategy('replace')} />
                  <span className="text-amber-400"><b>Replace</b> (overwrite — for fresh start)</span>
                </label>
              </div>
              <div className="rounded bg-emerald-500/10 border border-emerald-500/30 p-2.5 mb-3 flex gap-2 text-[11px]">
                <ShieldCheck size={13} className="text-emerald-400 shrink-0 mt-0.5" />
                <div className="text-emerald-200/80">
                  Auto-backup ON. Existing daily_data_2026 docs untuk channel ni akan disnapshot ke <code>backup_*</code> collection sebelum apa-apa write.
                </div>
              </div>
              <Button onClick={runApply} disabled={applying} className="bg-emerald-600 hover:bg-emerald-700" data-testid="recovery-apply-btn">
                {applying ? <Loader2 size={14} className="animate-spin mr-2" /> : <CheckCircle2 size={14} className="mr-2" />}
                Apply Recovery ({scanResult.sales_dates_recovered + scanResult.marketing_dates_recovered} entries)
              </Button>
            </div>
          )}

          {scanResult.errors && scanResult.errors.length > 0 && (
            <details className="text-[11px] text-amber-400">
              <summary className="cursor-pointer hover:text-amber-300">Parse warnings ({scanResult.errors.length})</summary>
              <ul className="mt-1 ml-4 list-disc">
                {scanResult.errors.map((e, i) => <li key={i} className="text-[10px]">{e}</li>)}
              </ul>
            </details>
          )}
        </div>
      )}
        </TabsContent>

        <TabsContent value="whatsapp" className="space-y-3 mt-3">
          <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-4 space-y-3">
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <MessageSquareText size={14} className="text-green-400" />
              Step 1: Paste WhatsApp KPI Messages
            </h3>
            <p className="text-[11px] text-[hsl(var(--text-muted))] leading-relaxed">
              Copy-paste 1 atau lebih mesej format <code className="text-white">Daily Target / KPI</code> dari WhatsApp group ke text area di bawah. Boleh paste banyak hari sekali — separated by blank line.
              <br />Parser akan auto-extract: date, leads, closing, dan per-broker (Active Client, Lot, Commission, Deposit). <span className="text-emerald-400 font-semibold">Elev8 = Octa</span> auto-mapped.
            </p>
            <textarea
              value={waText}
              onChange={(e) => setWaText(e.target.value)}
              placeholder="Daily Target / KPI&#10;27/2/26&#10;FGC&#10;&#10;(Sales)&#10;Sales Lead : 67/300&#10;..."
              className="w-full h-48 p-3 rounded-lg bg-[hsl(var(--card))] border border-[hsl(var(--border))] text-white text-[12px] font-mono"
              data-testid="wa-textarea"
            />
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className="text-[11px] text-[hsl(var(--text-muted))]">
                {waText.length > 0 ? `${waText.length} chars · ${waText.split(/\n\s*\n/).length} block(s)` : 'Paste mesej WhatsApp di sini'}
              </span>
              <Button onClick={parseWA} disabled={waParsing || !waText.trim()} className="bg-green-600 hover:bg-green-700" data-testid="wa-parse-btn">
                {waParsing ? <Loader2 size={14} className="animate-spin mr-2" /> : <RefreshCw size={14} className="mr-2" />}
                Parse Now
              </Button>
            </div>
          </div>

          {waParsed && (
            <div className="rounded-xl border border-green-500/30 bg-green-500/5 p-4 space-y-3" data-testid="wa-result">
              <h3 className="text-sm font-bold text-white flex items-center gap-2">
                <CheckCircle2 size={14} className="text-green-400" />
                Parse Result — {waParsed.total_parsed} entries from {waParsed.total_blocks} blocks
              </h3>

              {waParsed.entries.length > 0 && (
                <div className="overflow-x-auto rounded-md border border-[hsl(var(--border))]">
                  <table className="w-full text-[11px]">
                    <thead className="bg-[hsl(var(--card))] text-[10px] uppercase tracking-wider text-[hsl(var(--text-muted))]">
                      <tr>
                        <th className="text-left px-3 py-1.5">Date</th>
                        <th className="text-left px-3 py-1.5">Channel</th>
                        <th className="text-right px-3 py-1.5">Leads</th>
                        <th className="text-right px-3 py-1.5">Closed</th>
                        <th className="text-right px-3 py-1.5">Comm $</th>
                        <th className="text-left px-3 py-1.5">Brokers</th>
                      </tr>
                    </thead>
                    <tbody>
                      {waParsed.entries.map((e, i) => (
                        <tr key={i} className="border-t border-[hsl(var(--border))]">
                          <td className="px-3 py-1.5 font-mono text-white">{e.date}</td>
                          <td className="px-3 py-1.5 text-[hsl(var(--text-muted))]">{e.channel_hint || '—'}</td>
                          <td className="px-3 py-1.5 text-right font-mono">{e.sales_entry.totalLead}</td>
                          <td className="px-3 py-1.5 text-right font-mono">{e.sales_entry.totalClosed}</td>
                          <td className="px-3 py-1.5 text-right font-mono text-emerald-400">${e.sales_entry.total_commission_usd}</td>
                          <td className="px-3 py-1.5 text-[10px]">{e.brokers_found.join(', ').toUpperCase()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="border-t border-green-500/20 pt-3 space-y-2">
                <h4 className="text-sm font-bold text-white flex items-center gap-2">
                  <ArrowRight size={14} className="text-emerald-400" />
                  Step 2: Apply ke channel mana?
                </h4>
                <div className="flex items-center gap-2 flex-wrap">
                  <Select value={waTargetIb} onValueChange={setWaTargetIb}>
                    <SelectTrigger className="h-9 text-[12px] max-w-xs" data-testid="wa-target-select">
                      <SelectValue placeholder="-- Pilih target channel --" />
                    </SelectTrigger>
                    <SelectContent>
                      {ibs.map(ib => (
                        <SelectItem key={ib.id} value={ib.id}>{ib.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={waStrategy} onValueChange={setWaStrategy}>
                    <SelectTrigger className="h-9 text-[12px] w-[220px]" data-testid="wa-strategy-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="overwrite">Overwrite (replace existing)</SelectItem>
                      <SelectItem value="fill_zeros">Fill Zeros only (safe)</SelectItem>
                      <SelectItem value="merge">Merge (skip existing)</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={applyWA} disabled={waApplying || !waTargetIb} className="bg-emerald-600 hover:bg-emerald-700" data-testid="wa-apply-btn">
                    {waApplying ? <Loader2 size={14} className="animate-spin mr-2" /> : <CheckCircle2 size={14} className="mr-2" />}
                    Apply ({waParsed.entries.length})
                  </Button>
                </div>
                <p className="text-[10px] text-[hsl(var(--text-muted))]">
                  Auto-backup ON sebelum write. <b>Overwrite</b>: ganti semua entries pada tarikh yang sama. <b>Fill Zeros</b>: ganti entries yang nilai 0 sahaja (paling selamat). <b>Merge</b>: skip kalau tarikh dah ada.
                </p>
              </div>

              {waParsed.errors && waParsed.errors.length > 0 && (
                <details className="text-[11px] text-amber-400">
                  <summary className="cursor-pointer">Parse warnings ({waParsed.errors.length})</summary>
                  <ul className="mt-1 ml-4 list-disc">
                    {waParsed.errors.map((e, i) => <li key={i} className="text-[10px]">{e}</li>)}
                  </ul>
                </details>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* BACKUPS LIST */}
      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-4">
        <h3 className="text-sm font-bold text-white flex items-center gap-2 mb-2">
          <Database size={14} className="text-purple-400" />
          Backups Available ({backups.length})
        </h3>
        {backups.length === 0 ? (
          <p className="text-[11px] text-[hsl(var(--text-muted))]">No backups yet. Backups akan dicipta automatik bila anda Apply Recovery.</p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-[hsl(var(--border))]">
            <table className="w-full text-[11px]">
              <thead className="bg-[hsl(var(--card))] text-[10px] uppercase tracking-wider text-[hsl(var(--text-muted))]">
                <tr>
                  <th className="text-left px-3 py-1.5">Backup Collection</th>
                  <th className="text-right px-3 py-1.5">Docs</th>
                  <th className="text-right px-3 py-1.5">Action</th>
                </tr>
              </thead>
              <tbody>
                {backups.map(b => (
                  <tr key={b.name} className="border-t border-[hsl(var(--border))]">
                    <td className="px-3 py-1.5 font-mono text-white text-[10px]">{b.name}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-purple-400">{b.doc_count}</td>
                    <td className="px-3 py-1.5 text-right">
                      <Button
                        onClick={() => restoreBackup(b.name)}
                        disabled={restoring === b.name}
                        size="sm"
                        variant="outline"
                        className="h-6 text-[10px] border-purple-500/40 text-purple-400 hover:bg-purple-500/10"
                        data-testid={`restore-${b.name}`}
                      >
                        {restoring === b.name ? <Loader2 size={10} className="animate-spin" /> : <RotateCcw size={10} className="mr-1" />}
                        Restore
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
